import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useArticleFamilies, useCreateFamily, useUpdateFamily, useDeleteFamily, useArticles } from '@/hooks/use-articles';
import {
  Button, Card, CardContent, CardHeader,
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  EmptyState, FilterBar, FilterField, Input, Label, PageHeader,
  Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@erp/ui';
import { Plus, Trash2, Pencil, Search, FolderOpen } from '@/components/icons';
import { ArticleAssignDialog } from './ArticleAssignDialog';
import type { ArticleFamily } from '@erp/data-access/interfaces';

export function FamiliesPage() {
  const { t } = useTranslation();
  const { data: families, isLoading } = useArticleFamilies();
  const { data: articles } = useArticles();
  const createMutation = useCreateFamily();
  const updateMutation = useUpdateFamily();
  const deleteMutation = useDeleteFamily();

  const [search, setSearch] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<ArticleFamily | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ArticleFamily | null>(null);
  const [name, setName] = useState('');

  // Count articles per family
  const articleCountMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of articles ?? []) {
      if (a.family) map.set(a.family, (map.get(a.family) ?? 0) + 1);
    }
    return map;
  }, [articles]);

  // Filter by search
  const filtered = useMemo(() => {
    if (!search.trim()) return families ?? [];
    const s = search.toLowerCase();
    return (families ?? []).filter((f) => f.name.toLowerCase().includes(s));
  }, [families, search]);

  const openCreate = () => { setEditing(null); setName(''); setFormOpen(true); };
  const openEdit = (f: ArticleFamily) => { setEditing(f); setName(f.name); setFormOpen(true); };

  const handleSave = async () => {
    if (!name.trim()) return;
    if (editing) {
      await updateMutation.mutateAsync({ id: editing.id, name: name.trim() });
    } else {
      await createMutation.mutateAsync(name.trim());
    }
    setFormOpen(false); setEditing(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('articles.families')}
        actions={
          <Button onClick={openCreate}>
            <Plus data-icon="inline-start" size={16} />
            {t('articles.newFamily')}
          </Button>
        }
      />

      <Card className="transition-all duration-200 max-w-3xl">
        <CardHeader className="pb-3">
          <FilterBar onClear={search ? () => setSearch('') : undefined} clearLabel={t('common.clearFilters')}>
            <FilterField label={t('common.search')} grow>
              <div className="relative">
                <Search size={14} className="absolute top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none start-2.5" />
                <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} className="ps-8 h-8" />
              </div>
            </FilterField>
          </FilterBar>
        </CardHeader>
        <CardContent className="pt-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="h-11 bg-muted/50 px-3 text-xs font-medium select-none first:ps-3 sm:px-4 sm:text-sm sm:first:ps-5 text-start">{t('common.name')}</TableHead>
                <TableHead className="h-11 bg-muted/50 px-3 text-xs font-medium select-none sm:px-4 sm:text-sm text-end">{t('dashboard.articles')}</TableHead>
                <TableHead className="h-11 w-20 bg-muted/50 px-3 last:pe-3 sm:px-4 sm:last:pe-5" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`sk-${i}`} className="border-b border-border/60 last:border-b-0">
                    {Array.from({ length: 3 }).map((_, j) => (
                      <TableCell key={j} className="px-3 py-2 sm:px-4 sm:py-3">
                        <Skeleton className="h-4 w-full max-w-[140px] bg-muted/70" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : !filtered.length ? (
                <TableRow className="border-0">
                  <TableCell colSpan={3} className="px-4 py-10 text-center">
                    <EmptyState icon={<FolderOpen size={18} />} title={t('common.noResults')} action={<Button variant="outline" size="sm" onClick={openCreate}><Plus data-icon="inline-start" size={14} />{t('articles.newFamily')}</Button>
                    } />
                  </TableCell>
                </TableRow>
              ) : filtered.map((f) => (
                <TableRow
                  key={f.id}
                  className="border-b border-border/60 last:border-b-0 transition-colors hover:bg-muted/50"
                >
                  <TableCell className="px-3 py-2 text-xs first:ps-3 sm:px-4 sm:py-3 sm:text-sm sm:first:ps-5 font-medium">{f.name}</TableCell>
                  <TableCell className="px-3 py-2 text-xs sm:px-4 sm:py-3 sm:text-sm text-end text-muted-foreground">{articleCountMap.get(f.id) ?? 0}</TableCell>
                  <TableCell className="px-3 py-2 last:pe-3 sm:px-4 sm:py-3 sm:last:pe-5">
                    <div className="flex items-center gap-1 justify-end">
                      <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => { setAssignTarget(f); setAssignOpen(true); }}>
                        <Plus size={12} className="me-1" />{t('articles.assignArticle')}
                      </Button>
                      <Button variant="ghost" size="icon-xs" onClick={() => openEdit(f)}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="icon-xs" onClick={() => deleteMutation.mutate(f.id)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editing ? t('common.edit') : t('articles.newFamily')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>{t('common.name')} *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} disabled={!name.trim() || createMutation.isPending || updateMutation.isPending}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {assignTarget && (
        <ArticleAssignDialog
          open={assignOpen}
          onOpenChange={setAssignOpen}
          field="family"
          targetId={assignTarget.id}
          title={`${t('articles.assignArticle')} — ${assignTarget.name}`}
        />
      )}
    </div>
  );
}
